// Pixar Whiteboard Ad's "Script" (live's `script-section`, `presenter-fields`, `room-fields`,
// `clip-lines`, `line-row` and `end-card-fields`; not captured, built from live's code, spec
// G14-pixar-whiteboard-ad PR g1): the presenter and the room, folded; the ten lines under their
// clips, each as written and as spoken with its board headline and icon; then the end card.
import {
  ActionIcon,
  Collapse,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { ChevronDown, PencilLine, WandSparkles } from "lucide-react";
import { useState, type ReactNode } from "react";

import type {
  CastPronoun,
  WhiteboardEndCard,
  WhiteboardPresenter,
  WhiteboardRoom,
  WhiteboardScript,
  WhiteboardScriptLine,
  WhiteboardTimingPlan,
} from "../../../api/generated/model";
import { GENERATE_SCRIPT } from "../../../platform/scripts/ScriptActions";
import { FieldLabel } from "../../../ui/FieldLabel";
import {
  CLIPS,
  CLIP_NOTES,
  EMPTY_SCRIPT_HINT,
  EMPTY_SCRIPT_WRITE_HINT,
  END_CARD_NOTE,
  END_CARD_PLACEHOLDERS,
  END_CARD_TITLE,
  HEADLINE_MAX_CHARS,
  HEADLINE_PLACEHOLDER,
  ICON_PLACEHOLDER,
  LAST_LINE_ID,
  LAST_LINE_NOTE,
  MAX_LINE_LENGTH,
  MAX_WORDS_PER_LINE,
  PRESENTER_NOTE,
  PRESENTER_PLACEHOLDERS,
  PRESENTER_TITLE,
  PRODUCT_LINE_ID,
  PRODUCT_LINE_NOTE,
  ROLE_LABELS,
  ROOM_NOTE,
  ROOM_PLACEHOLDERS,
  ROOM_TITLE,
  SCRIPT_TOOLTIP,
  SPOKEN_LABEL,
  SPOKEN_PLACEHOLDER,
  SPOKEN_TOOLTIP,
  TEXT_PLACEHOLDER,
  WRITE_SCRIPT,
  clipMetaLabel,
  lineWords,
  scriptMetaLabel,
} from "./pixar-whiteboard-script";
import classes from "./podcast.module.css";
import whiteboard from "./whiteboard.module.css";

const PRONOUNS: readonly CastPronoun[] = ["she", "he"];
/** The fields each block shows, in live's order, those live gives two rows marked. */
const PRESENTER_FIELDS = ["identity", "outfit", "personality", "voice"] as const;
const ROOM_FIELDS = ["place", "description", "palette", "light", "why"] as const;
const END_CARD_FIELDS = ["headline", "support_line", "cta_button"] as const;
const TWO_ROWS: ReadonlySet<string> = new Set(["voice", "description"]);

/** Live's folded block (`CollapsibleSection`, closed at first): its title and a chevron. */
function Folded({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  const [opened, setOpened] = useState(false);
  return (
    <div className={whiteboard.section}>
      <UnstyledButton className={whiteboard.sectionHeader} aria-expanded={opened} onClick={() => setOpened((open) => !open)}>
        <span>{title}</span>
        <ChevronDown size={16} className={whiteboard.chevron} data-open={opened || undefined} aria-hidden />
      </UnstyledButton>
      <Collapse expanded={opened}>
        <Stack gap={4} className={whiteboard.sectionBody}>
          <Text className={whiteboard.note}>{note}</Text>
          {children}
        </Stack>
      </Collapse>
    </div>
  );
}

interface FieldProps {
  name: string;
  field: string;
  placeholder: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** A block's text field: live's small input, or its small two-row textarea. */
function Field({ name, field, placeholder, value, disabled, onChange }: FieldProps) {
  const label = `${name} ${field.replace("_", " ")}`;
  if (TWO_ROWS.has(field)) {
    return (
      <Textarea
        size="xs"
        autosize
        minRows={2}
        aria-label={label}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    );
  }
  return (
    <TextInput
      size="xs"
      aria-label={label}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

function PresenterFields(props: { presenter: WhiteboardPresenter; disabled: boolean; onChange: (presenter: WhiteboardPresenter) => void }) {
  const { presenter, disabled, onChange } = props;
  return (
    <Folded title={PRESENTER_TITLE} note={PRESENTER_NOTE}>
      <Select
        size="xs"
        w={96}
        aria-label="Pronoun"
        data={[...PRONOUNS]}
        value={presenter.pronoun}
        allowDeselect={false}
        disabled={disabled}
        onChange={(next) => {
          const pronoun = PRONOUNS.find((each) => each === next);
          if (pronoun !== undefined) onChange({ ...presenter, pronoun });
        }}
      />
      {PRESENTER_FIELDS.map((field) => (
        <Field
          key={field}
          name={PRESENTER_TITLE}
          field={field}
          placeholder={PRESENTER_PLACEHOLDERS[field]}
          value={presenter[field]}
          disabled={disabled}
          onChange={(value) => onChange({ ...presenter, [field]: value })}
        />
      ))}
    </Folded>
  );
}

function RoomFields(props: { room: WhiteboardRoom; disabled: boolean; onChange: (room: WhiteboardRoom) => void }) {
  const { room, disabled, onChange } = props;
  return (
    <Folded title={ROOM_TITLE} note={ROOM_NOTE}>
      {ROOM_FIELDS.map((field) => (
        <Field
          key={field}
          name={ROOM_TITLE}
          field={field}
          placeholder={ROOM_PLACEHOLDERS[field]}
          value={room[field]}
          disabled={disabled}
          onChange={(value) => onChange({ ...room, [field]: value })}
        />
      ))}
    </Folded>
  );
}

function EndCardFields(props: { endCard: WhiteboardEndCard; disabled: boolean; onChange: (endCard: WhiteboardEndCard) => void }) {
  const { endCard, disabled, onChange } = props;
  return (
    <Stack gap={4}>
      <Group gap={8} className={whiteboard.endCardHeader}>
        <Text className={whiteboard.title}>{END_CARD_TITLE}</Text>
        <Text className={whiteboard.note}>{END_CARD_NOTE}</Text>
      </Group>
      {END_CARD_FIELDS.map((field) => (
        <Field
          key={field}
          name={END_CARD_TITLE}
          field={field}
          placeholder={END_CARD_PLACEHOLDERS[field]}
          value={endCard[field]}
          disabled={disabled}
          onChange={(value) => onChange({ ...endCard, [field]: value })}
        />
      ))}
    </Stack>
  );
}

interface LineRowProps {
  line: WhiteboardScriptLine;
  /** The spoken words the plan last counted; none before its first answer. */
  words: number | null;
  disabled: boolean;
  onChange: (line: WhiteboardScriptLine) => void;
}

/**
 * Live's line: its number and role; the sentence as written and as spoken; its board line, the
 * headline in capitals as typed with its count and the icon, live's note in the icon's place on
 * the product line and in the whole board line's on the last; and its spoken words.
 */
function LineRow({ line, words, disabled, onChange }: LineRowProps) {
  const headline = line.board.headline;
  const name = `Line ${line.id}`;
  return (
    <div className={whiteboard.row}>
      <span className={classes.number}>{line.id}</span>
      <span className={whiteboard.role} title={ROLE_LABELS[line.role]}>
        {ROLE_LABELS[line.role]}
      </span>
      <Stack gap={4} className={whiteboard.body}>
        <Textarea
          classNames={{ input: classes.lineInput }}
          variant="unstyled"
          autosize
          minRows={1}
          maxLength={MAX_LINE_LENGTH}
          aria-label={`${name}, as written`}
          placeholder={TEXT_PLACEHOLDER}
          value={line.text}
          disabled={disabled}
          onChange={(event) => onChange({ ...line, text: event.currentTarget.value })}
        />
        <Tooltip label={SPOKEN_TOOLTIP} position="top-start" multiline maw={320} withArrow>
          <div className={whiteboard.spoken}>
            <Text className={whiteboard.spokenLabel}>{SPOKEN_LABEL}</Text>
            <Textarea
              className={whiteboard.spokenText}
              classNames={{ input: whiteboard.spokenInput }}
              variant="unstyled"
              autosize
              minRows={1}
              maxLength={MAX_LINE_LENGTH}
              aria-label={`${name}, as spoken`}
              placeholder={SPOKEN_PLACEHOLDER}
              value={line.spoken}
              disabled={disabled}
              onChange={(event) => onChange({ ...line, spoken: event.currentTarget.value })}
            />
          </div>
        </Tooltip>
        {line.id === LAST_LINE_ID ? (
          <Text className={whiteboard.note}>{LAST_LINE_NOTE}</Text>
        ) : (
          <Group gap={8} wrap="nowrap" align="center">
            <TextInput
              size="xs"
              className={whiteboard.headline}
              aria-label={`${name}, board headline`}
              placeholder={HEADLINE_PLACEHOLDER}
              value={headline}
              disabled={disabled}
              rightSectionWidth={44}
              rightSection={
                <Text className={classes.count} data-over={headline.length > HEADLINE_MAX_CHARS || undefined}>
                  {headline.length}/{HEADLINE_MAX_CHARS}
                </Text>
              }
              onChange={(event) => {
                const input = event.currentTarget;
                const caret = input.selectionStart;
                const typed = input.value.toUpperCase();
                input.value = typed;
                input.setSelectionRange(caret, caret);
                onChange({ ...line, board: { ...line.board, headline: typed } });
              }}
            />
            {line.id === PRODUCT_LINE_ID ? (
              <Text className={whiteboard.note}>{PRODUCT_LINE_NOTE}</Text>
            ) : (
              <TextInput
                size="xs"
                className={whiteboard.icon}
                aria-label={`${name}, board icon`}
                placeholder={ICON_PLACEHOLDER}
                value={line.board.icon}
                disabled={disabled}
                onChange={(event) => onChange({ ...line, board: { ...line.board, icon: event.currentTarget.value } })}
              />
            )}
          </Group>
        )}
      </Stack>
      {words === null ? null : (
        <Text className={classes.count} data-over={words > MAX_WORDS_PER_LINE || undefined}>
          {words}/{MAX_WORDS_PER_LINE}
        </Text>
      )}
    </div>
  );
}

export interface WhiteboardScriptSectionProps {
  /** The script, or none before it is generated or written. */
  script: WhiteboardScript | null;
  /** The script's timing plan as the server last read it: the clips' lengths, its warnings. */
  timing: WhiteboardTimingPlan | null;
  /** A director reads the pack or writes the script: live folds the editor to its loader. */
  writing: boolean;
  /** Nothing changes: a director works, or the script is approved. */
  locked: boolean;
  /** What keeps the script from approval, once its lines are edited (live's error line). */
  issue: string | null;
  /** Live's warning: an unreadable pack, the second script's problems. */
  warning: string | null;
  onGenerate: () => void;
  onWrite: () => void;
  onChange: (script: WhiteboardScript) => void;
}

/**
 * Live's "Script": its info tooltip; at the right the plan's meta line, the wand (Generate Script)
 * and the pencil (Write the 10 lines yourself). Without a script, or while a director writes,
 * live's hint; with one, the presenter, the room, the lines by clip and the end card. The plan's
 * warnings, the issue and the warning follow.
 */
export function WhiteboardScriptSection(props: WhiteboardScriptSectionProps) {
  const { script, timing, writing, locked, issue, warning, onGenerate, onWrite, onChange } = props;
  const setLine = (next: WhiteboardScriptLine) => {
    if (script !== null) onChange({ ...script, lines: script.lines.map((each) => (each.id === next.id ? next : each)) });
  };
  const planWarning = timing?.warnings.join(" ") ?? "";
  return (
    <section aria-label="Script">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Script" info={SCRIPT_TOOLTIP} spoken />
        </Text>
        <Group gap={8} wrap="nowrap">
          {timing === null || script === null ? null : <Text className={classes.meta}>{scriptMetaLabel(timing)}</Text>}
          <Tooltip label={GENERATE_SCRIPT} withArrow>
            <ActionIcon variant="subtle" size={36} aria-label={GENERATE_SCRIPT} disabled={locked} onClick={onGenerate}>
              <WandSparkles size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={WRITE_SCRIPT} withArrow>
            <ActionIcon variant="subtle" size={36} aria-label={WRITE_SCRIPT} disabled={locked} onClick={onWrite}>
              <PencilLine size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
        </Group>
      </div>
      <div className={classes.editor} data-busy={writing || undefined}>
        {script === null || writing ? (
          <Text className={classes.hint}>
            Press
            <WandSparkles size={14} className={classes.hintIcon} aria-label="the wand" />
            {EMPTY_SCRIPT_HINT}
            <PencilLine size={14} className={classes.hintIcon} aria-label="the pencil" />
            {EMPTY_SCRIPT_WRITE_HINT}
          </Text>
        ) : (
          <Stack gap={8}>
            <PresenterFields
              presenter={script.presenter}
              disabled={locked}
              onChange={(presenter) => onChange({ ...script, presenter })}
            />
            <RoomFields room={script.room} disabled={locked} onChange={(room) => onChange({ ...script, room })} />
            <Stack gap={0}>
              {CLIPS.map((clip) => {
                const clipTiming = timing?.clips.find((each) => each.clip === clip);
                return (
                  <Stack key={clip} gap={0}>
                    <Group gap={8} className={whiteboard.clipHeader}>
                      <Text className={whiteboard.clip}>{CLIP_NOTES[clip]}</Text>
                      {clipTiming === undefined ? null : <Text className={whiteboard.note}>{clipMetaLabel(clipTiming)}</Text>}
                    </Group>
                    {script.lines
                      .filter((line) => line.clip === clip)
                      .map((line) => (
                        <LineRow key={line.id} line={line} words={lineWords(timing, line.id)} disabled={locked} onChange={setLine} />
                      ))}
                  </Stack>
                );
              })}
            </Stack>
            <EndCardFields
              endCard={script.end_card}
              disabled={locked}
              onChange={(endCard) => onChange({ ...script, end_card: endCard })}
            />
          </Stack>
        )}
        {writing ? (
          <div className={classes.editorLoader}>
            <Loader size="sm" aria-label="Writing the script" />
          </div>
        ) : null}
      </div>
      {planWarning === "" ? null : <Text className={classes.issue}>{planWarning}</Text>}
      {issue === null ? null : <Text className={classes.issue}>{issue}</Text>}
      {warning === null ? null : <Text className={classes.warning}>{warning}</Text>}
    </section>
  );
}
